import { useState, useMemo, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbarContext } from "components/snackbar";
import { EditorRef } from "react-email-editor";
import { IEmailTemplate, IMergeField, TemplateType } from "../../types/IEmailTemplate";
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  getMergeFields,
} from "../../api/templates";

interface TemplateEditorErrors {
  name?: string;
  subject?: string;
  messageText?: string;
}

export const useTemplateEditor = (
  emailEditorRef: React.RefObject<EditorRef>
) => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const snackBar = useSnackbarContext();

  const isEditing = Boolean(id);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [templateType, setTemplateTypeState] = useState<TemplateType>("email");
  const [messageText, setMessageText] = useState("");
  const [touched, setTouched] = useState({
    name: false,
    subject: false,
    messageText: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  const setTemplateType = useCallback(
    (type: TemplateType) => {
      if (!isEditing) {
        setTemplateTypeState(type);
      }
    },
    [isEditing]
  );

  const errors: TemplateEditorErrors = useMemo(() => {
    const errs: TemplateEditorErrors = {};
    if (touched.name && !name.trim()) {
      errs.name = "Template name is required";
    }
    if (templateType === "email" && touched.subject && !subject.trim()) {
      errs.subject = "Subject line is required";
    }
    if (templateType === "sms" && touched.messageText && !messageText.trim()) {
      errs.messageText = "Message text is required";
    }
    return errs;
  }, [name, subject, messageText, templateType, touched]);

  const {
    data: template,
    isLoading: isLoadingTemplate,
  } = useQuery<IEmailTemplate>(
    ["email-template", id],
    () => getTemplate(id!),
    {
      enabled: isEditing,
      onSuccess: (data) => {
        setName(data.name);
        setSubject(data.subject || "");
        setTemplateTypeState(data.templateType || "email");
        setMessageText(data.messageText || "");
      },
    }
  );

  const {
    data: mergeFields = [],
    isLoading: isLoadingMergeFields,
  } = useQuery<IMergeField[]>(
    ["merge-fields", templateType],
    () => getMergeFields(templateType === "sms" ? "sms" : undefined),
  );

  const mergeTags = useMemo(() => {
    return mergeFields.reduce<Record<string, { name: string; value: string }>>(
      (acc, field) => ({
        ...acc,
        [field.key]: { name: field.label, value: `{{${field.key}}}` },
      }),
      {}
    );
  }, [mergeFields]);

  const createMutation = useMutation(
    (data: {
      templateType?: TemplateType;
      name: string;
      subject?: string;
      designJson?: Record<string, unknown>;
      htmlContent?: string;
      messageText?: string;
    }) => createTemplate(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("email-templates");
        snackBar.showSnackbar("Template created successfully");
        history.push("/outreach/templates");
      },
      onError: () => {
        snackBar.showSnackbar("Failed to create template");
      },
      onSettled: () => {
        setIsSaving(false);
      },
    }
  );

  const updateMutation = useMutation(
    (data: {
      id: string;
      payload: Partial<{
        name: string;
        subject: string;
        designJson: Record<string, unknown>;
        htmlContent: string;
        messageText: string;
      }>;
    }) => updateTemplate(data.id, data.payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("email-templates");
        queryClient.invalidateQueries(["email-template", id]);
        snackBar.showSnackbar("Template updated successfully");
        history.push("/outreach/templates");
      },
      onError: () => {
        snackBar.showSnackbar("Failed to update template");
      },
      onSettled: () => {
        setIsSaving(false);
      },
    }
  );

  const handleSave = useCallback(() => {
    setTouched({ name: true, subject: true, messageText: true });

    if (!name.trim()) {
      return;
    }

    // SMS save path
    if (templateType === "sms") {
      if (!messageText.trim()) {
        return;
      }

      setIsSaving(true);

      if (isEditing && id) {
        updateMutation.mutate({
          id,
          payload: {
            name: name.trim(),
            messageText: messageText.trim(),
          },
        });
      } else {
        createMutation.mutate({
          templateType: "sms",
          name: name.trim(),
          messageText: messageText.trim(),
        });
      }
      return;
    }

    // Email save path
    if (!subject.trim()) {
      return;
    }

    if (!emailEditorRef.current?.editor) {
      snackBar.showSnackbar("Editor is not ready yet");
      return;
    }

    setIsSaving(true);

    emailEditorRef.current.editor.exportHtml((data) => {
      const { design, html } = data;

      if (isEditing && id) {
        updateMutation.mutate({
          id,
          payload: {
            name: name.trim(),
            subject: subject.trim(),
            designJson: design as unknown as Record<string, unknown>,
            htmlContent: html,
          },
        });
      } else {
        createMutation.mutate({
          templateType: "email",
          name: name.trim(),
          subject: subject.trim(),
          designJson: design as unknown as Record<string, unknown>,
          htmlContent: html,
        });
      }
    });
  }, [
    name,
    subject,
    templateType,
    messageText,
    isEditing,
    id,
    emailEditorRef,
    createMutation,
    updateMutation,
    snackBar,
  ]);

  const isLoading = isLoadingTemplate || isLoadingMergeFields;

  return {
    name,
    setName,
    subject,
    setSubject,
    templateType,
    setTemplateType,
    messageText,
    setMessageText,
    isLoading,
    isSaving,
    mergeFields,
    mergeTags,
    handleSave,
    errors,
    touched,
    setTouched,
    template,
    isEditing,
  };
};
